import { useEffect, useState } from "react";
import api from "@/lib/api";
import KeyData from "@/components/KeyData";
import { useLang } from "@/context/LanguageContext";
import { Check, Flame } from "lucide-react";
import { toast } from "sonner";

export default function TodaysPractice({ onStreakChange }) {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    api.get("/today/practice").then(r => {
      setData(r.data);
      onStreakChange?.(r.data.current_streak);
    }).catch(() => {});
  }, [lang]);

  if (!data) {
    return <div className="vedari-card mb-6 h-44 animate-soft-pulse" />;
  }

  const p = data.practice;
  const completed = data.completed;

  const markDone = async () => {
    if (marking || completed) return;
    setMarking(true);
    try {
      const r = await api.post(`/practices/${p.practice_id}/complete`);
      setData(d => ({ ...d, completed: true, current_streak: r.data.current_streak, longest_streak: r.data.longest_streak, total_completed: r.data.total_completed }));
      onStreakChange?.(r.data.current_streak);
      if (r.data.ack) {
        toast.success(t("practice.ack", { n: r.data.current_streak }));
      } else {
        toast.success(t("practice.done"));
      }
    } catch {
      toast.error("Could not mark practice");
    } finally {
      setMarking(false);
    }
  };

  return (
    <section
      className="vedari-card mb-6"
      data-testid="today-practice-card"
      style={{ border: "1px solid rgba(184,149,74,0.35)", boxShadow: "0 0 0 1px rgba(184,149,74,0.06)" }}
    >
      <div className="flex items-center justify-between">
        <p className="text-caption tracking-[0.22em] uppercase" style={{ color: "var(--vedari-gold)" }}>
          {t("practice.heading")}
        </p>
        <div className="flex items-center gap-2">
          {data.arc_day && (
            <span className="text-[10px] tracking-wider uppercase font-medium" style={{ color: "var(--vedari-gold)", opacity: 0.85 }} data-testid="practice-arc-badge">
              {lang === "hi" ? `दिन ${data.arc_day} / ${data.arc_total}` : `Day ${data.arc_day} of ${data.arc_total}`}
            </span>
          )}
          {data.current_streak > 0 && (
            <span className="inline-flex items-center gap-1 text-caption" style={{ color: "var(--vedari-gold)" }} data-testid="practice-streak-badge">
              <Flame size={13} strokeWidth={1.75} />
              <KeyData>{data.current_streak}</KeyData>
            </span>
          )}
        </div>
      </div>

      {data.personalized_intro && (
        <p className="font-serif italic text-body-sm mt-3 leading-relaxed" style={{ color: "var(--vedari-ink-soft)" }} data-testid="practice-intro">
          {data.personalized_intro}
        </p>
      )}

      <h3 className="font-serif text-h2 mt-3 leading-tight" data-testid="practice-title">
        {p.title}
      </h3>
      <p className="text-body-lg mt-3 leading-relaxed" data-testid="practice-instruction">
        {p.instruction}
      </p>
      <p className="text-caption mt-3" style={{ color: "var(--vedari-ink-soft)" }}>
        <KeyData>{p.duration_minutes}</KeyData> {lang === "hi" ? "मिनट" : "min"}
      </p>

      <button
        onClick={markDone}
        disabled={completed || marking}
        className={`mt-5 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full transition-all ${completed ? "opacity-100" : "active:scale-[0.98]"}`}
        style={{
          backgroundColor: completed ? "rgba(184,149,74,0.15)" : "var(--vedari-gold)",
          color: completed ? "var(--vedari-gold)" : "var(--vedari-cream)",
          border: completed ? "1px solid var(--vedari-gold)" : "none",
          fontWeight: 500,
          letterSpacing: "0.02em",
        }}
        data-testid="practice-mark-done-btn"
      >
        {completed ? <><Check size={16} strokeWidth={2} /> {t("practice.done")}</> : t("practice.mark_done")}
      </button>
    </section>
  );
}
